import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { isAuthenticated, getSession, logout } from "../utils/auth";
import { fetchSubmissions } from "../utils/api";
import DashboardSidebar from "../components/DashboardSidebar";

/* ---- Helpers ---- */
function parseDate(str) {
  if (!str) return null;
  // handle DD/MM/YYYY or ISO
  if (str.includes("/")) {
    const [d, m, y] = str.split("/");
    return new Date(+y, +m - 1, +d);
  }
  return new Date(str);
}
function isSameMonth(d, ref) {
  return d && d.getMonth() === ref.getMonth() && d.getFullYear() === ref.getFullYear();
}
function isSameWeek(d, ref) {
  const startOfWeek = new Date(ref);
  startOfWeek.setDate(ref.getDate() - ref.getDay());
  startOfWeek.setHours(0, 0, 0, 0);
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 7);
  return d >= startOfWeek && d < endOfWeek;
}
function monthLabel(d) {
  return d.toLocaleString("default", { month: "short", year: "2-digit" });
}

/* ---- Stat Card ---- */
function StatCard({ icon, label, value, trend, trendUp, color }) {
  return (
    <div className="stat-card" style={{ "--stat-accent": color }}>
      <div className="stat-icon">{icon}</div>
      <div className="stat-body">
        <span className="stat-value">{value}</span>
        <span className="stat-label">{label}</span>
      </div>
      {trend !== undefined && (
        <span className={`stat-trend ${trendUp ? "up" : "down"}`}>
          {trendUp ? "↑" : "↓"} {trend}
        </span>
      )}
    </div>
  );
}

/* ---- Mini Bar Chart (pure CSS) ---- */
function BarChart({ data, title }) {
  const max = Math.max(...data.map((d) => d.value), 1);
  return (
    <div className="chart-card">
      <h4 className="chart-title">{title}</h4>
      <div className="bar-chart">
        {data.map((d, i) => (
          <div className="bar-col" key={i}>
            <div className="bar-fill" style={{ height: `${(d.value / max) * 100}%` }}>
              <span className="bar-val">{d.value}</span>
            </div>
            <span className="bar-label">{d.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---- Donut Chart (SVG) ---- */
function DonutChart({ segments, title, total }) {
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;
  const colors = ["#0066ff", "#00c853", "#ff9800", "#e53935", "#9c27b0", "#00bcd4", "#795548"];
  return (
    <div className="chart-card">
      <h4 className="chart-title">{title}</h4>
      <div className="donut-wrap">
        <svg viewBox="0 0 128 128" className="donut-svg">
          {segments.map((seg, i) => {
            const pct = total > 0 ? seg.value / total : 0;
            const dash = pct * circumference;
            const gap = circumference - dash;
            const el = (
              <circle
                key={i}
                cx="64" cy="64" r={radius}
                fill="none"
                stroke={colors[i % colors.length]}
                strokeWidth="14"
                strokeDasharray={`${dash} ${gap}`}
                strokeDashoffset={-offset}
                style={{ transition: "stroke-dasharray 0.6s ease" }}
              />
            );
            offset += dash;
            return el;
          })}
          <text x="64" y="60" textAnchor="middle" className="donut-total">{total}</text>
          <text x="64" y="74" textAnchor="middle" className="donut-sub">Total</text>
        </svg>
        <div className="donut-legend">
          {segments.map((seg, i) => (
            <div className="legend-item" key={i}>
              <span className="legend-dot" style={{ background: colors[i % colors.length] }}></span>
              <span className="legend-label">{seg.label}</span>
              <span className="legend-val">{seg.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---- Activity Feed ---- */
function ActivityFeed({ items }) {
  return (
    <div className="chart-card activity-card">
      <h4 className="chart-title">Recent Activity</h4>
      <div className="activity-list">
        {items.length === 0 && <p className="text-muted" style={{ padding: 16 }}>No recent activity</p>}
        {items.map((it, i) => (
          <div className="activity-item" key={i}>
            <div className="activity-dot"></div>
            <div className="activity-content">
              <span className="activity-name">{it.participantName || "Unknown"}</span>
              <span className="activity-meta">
                {it.nameOfTheHospital || ""} &middot; {it.dateOfSubmit || it.createdOn || ""}
              </span>
            </div>
            <span className={`badge ${it.isActive ? "badge-success" : "badge-secondary"}`}>
              {it.isActive ? "Active" : "Inactive"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [sortCol, setSortCol] = useState("participantName");
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [tab, setTab] = useState("overview");

  const session = isAuthenticated() ? getSession() : null;

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate("/login");
    }
  }, [navigate]);

  useEffect(() => {
    const requestedTab = searchParams.get("tab");
    if (["overview", "submissions", "analytics"].includes(requestedTab) && requestedTab !== tab) {
      setTab(requestedTab);
      return;
    }

    if (!requestedTab && tab !== "overview") {
      setTab("overview");
    }
  }, [searchParams, tab]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await fetchSubmissions();
      setData(result);
    } catch (err) {
      setError("Error loading data: " + (err.message || String(err)));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 60000);
    return () => clearInterval(interval);
  }, [loadData]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  /* ---- Computed Stats ---- */
  const stats = useMemo(() => {
    const now = new Date();
    const total = data.length;
    const active = data.filter((d) => d.isActive).length;
    const thisMonth = data.filter((d) => {
      const dt = parseDate(d.dateOfSubmit || d.createdOn);
      return dt && isSameMonth(dt, now);
    }).length;
    const thisWeek = data.filter((d) => {
      const dt = parseDate(d.dateOfSubmit || d.createdOn);
      return dt && isSameWeek(dt, now);
    }).length;

    // By hospital
    const byHospital = {};
    data.forEach((d) => {
      const h = d.nameOfTheHospital || "Unknown";
      byHospital[h] = (byHospital[h] || 0) + 1;
    });
    const hospitalSegments = Object.entries(byHospital)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([label, value]) => ({ label, value }));

    // By month (last 6 months)
    const monthlyMap = {};
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      monthlyMap[monthLabel(d)] = 0;
    }
    data.forEach((d) => {
      const dt = parseDate(d.dateOfSubmit || d.createdOn);
      if (dt) {
        const key = monthLabel(dt);
        if (key in monthlyMap) monthlyMap[key]++;
      }
    });
    const monthlyData = Object.entries(monthlyMap).map(([label, value]) => ({ label, value }));

    // By person taking consent
    const byPerson = {};
    data.forEach((d) => {
      const p = d.personTakingConsent || "Unknown";
      byPerson[p] = (byPerson[p] || 0) + 1;
    });
    const personData = Object.entries(byPerson)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([label, value]) => ({ label, value }));

    // Recent 8
    const recent = [...data]
      .sort((a, b) => {
        const da = parseDate(a.createdOn || a.dateOfSubmit);
        const db = parseDate(b.createdOn || b.dateOfSubmit);
        return (db || 0) - (da || 0);
      })
      .slice(0, 8);

    const uniqueHospitals = Object.keys(byHospital).length;

    return { total, active, thisMonth, thisWeek, hospitalSegments, monthlyData, personData, recent, uniqueHospitals };
  }, [data]);

  // Filter
  const filtered = data.filter((item) => {
    const q = search.toLowerCase();
    return (
      (item.participantName || "").toLowerCase().includes(q) ||
      (item.mobileNumber || "").toLowerCase().includes(q) ||
      (item.emailId || "").toLowerCase().includes(q) ||
      (item.mrnOrHospitalId || "").toLowerCase().includes(q) ||
      (item.nameOfTheHospital || "").toLowerCase().includes(q)
    );
  });

  // Sort
  const sorted = [...filtered].sort((a, b) => {
    const aVal = (a[sortCol] || "").toString().toLowerCase();
    const bVal = (b[sortCol] || "").toString().toLowerCase();
    if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
    if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  // Paginate
  const totalPages = Math.ceil(sorted.length / pageSize);
  const paged = sorted.slice(page * pageSize, (page + 1) * pageSize);

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortCol(col);
      setSortDir("asc");
    }
  };

  const sortIcon = (col) => {
    if (sortCol !== col) return " ↕";
    return sortDir === "asc" ? " ↑" : " ↓";
  };

  const columns = [
    { key: "participantId", label: "ID" },
    { key: "participantName", label: "Participant" },
    { key: "dateOfSubmit", label: "Date" },
    { key: "mobileNumber", label: "Mobile" },
    { key: "emailId", label: "Email" },
    { key: "mrnOrHospitalId", label: "MRN" },
    { key: "personTakingConsent", label: "Consent By" },
    { key: "nameOfTheHospital", label: "Hospital" },
    { key: "isActive", label: "Status" },
  ];

  const handleExportCSV = () => {
    if (sorted.length === 0) return;
    const headers = columns.map((c) => c.label);
    const rows = sorted.map((item) =>
      columns.map((c) => {
        if (c.key === "isActive") return item.isActive ? "Active" : "Inactive";
        return (item[c.key] || "").toString().replace(/,/g, " ");
      })
    );
    const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "submissions.csv";
    link.click();
  };

  return (
    <div className="page-dashboard">
      <DashboardSidebar onLogout={handleLogout} />

      {/* Main */}
      <div className="dash-body">
        <nav className="dash-topbar">
          <div className="topbar-left">
            <h2 className="topbar-title">
              {tab === "overview" ? "Dashboard" : tab === "submissions" ? "Consent Forms" : "Analytics"}
            </h2>
            <span className="topbar-greeting">Welcome back, {session?.displayName || session?.username || "User"}</span>
          </div>
          <div className="topbar-right">
            <button className="btn-outline-action btn-sm" onClick={loadData} title="Refresh">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
              Refresh
            </button>
          </div>
        </nav>

        {loading && <div className="dash-loading"><div className="spinner"></div>Loading data&hellip;</div>}
        {error && <div className="form-alert alert-error" style={{ margin: "24px 0" }}>{error}</div>}

        {!loading && !error && (
          <>
            {/* ===== OVERVIEW TAB ===== */}
            {tab === "overview" && (
              <div className="dash-content fade-in">
                {/* KPI Cards */}
                <div className="stats-row">
                  <StatCard
                    icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>}
                    label="Total Submissions"
                    value={stats.total}
                    color="#0066ff"
                  />
                  <StatCard
                    icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>}
                    label="Active Consents"
                    value={stats.active}
                    trend={stats.total > 0 ? `${Math.round((stats.active / stats.total) * 100)}%` : "0%"}
                    trendUp={stats.active > 0}
                    color="#00c853"
                  />
                  <StatCard
                    icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>}
                    label="This Month"
                    value={stats.thisMonth}
                    trend={`${stats.thisWeek} this week`}
                    trendUp={stats.thisWeek > 0}
                    color="#ff9800"
                  />
                  <StatCard
                    icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>}
                    label="Hospitals"
                    value={stats.uniqueHospitals}
                    color="#9c27b0"
                  />
                </div>

                {/* Charts Row */}
                <div className="charts-row">
                  <BarChart data={stats.monthlyData} title="Monthly Submissions (Last 6 Months)" />
                  <DonutChart segments={stats.hospitalSegments} title="By Hospital" total={stats.total} />
                </div>

                {/* Recent + Top Consent Takers */}
                <div className="charts-row">
                  <ActivityFeed items={stats.recent} />
                  <BarChart data={stats.personData} title="Top Consent Takers" />
                </div>
              </div>
            )}

            {/* ===== SUBMISSIONS TAB ===== */}
            {tab === "submissions" && (
              <div className="dash-content fade-in">
                <div className="dash-card">
                  <div className="dash-table-header">
                    <h5>Consent Forms List ({sorted.length})</h5>
                    <div className="dash-table-controls">
                      <input
                        type="text"
                        placeholder="Search consent forms..."
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                        className="dash-search"
                      />
                      <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }} className="dash-page-size">
                        <option value={10}>10 rows</option>
                        <option value={25}>25 rows</option>
                        <option value={50}>50 rows</option>
                        <option value={100}>100 rows</option>
                      </select>
                      <button className="btn-outline-action btn-sm" onClick={handleExportCSV}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                        Export CSV
                      </button>
                    </div>
                  </div>

                  <div className="table-responsive">
                    <table className="premium-table">
                      <thead>
                        <tr>
                          {columns.map((col) => (
                            <th key={col.key} onClick={() => handleSort(col.key)} style={{ cursor: "pointer", whiteSpace: "nowrap" }}>
                              {col.label}{sortIcon(col.key)}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {paged.length === 0 ? (
                          <tr><td colSpan={columns.length} className="text-center text-muted">No consent forms found</td></tr>
                        ) : (
                          paged.map((item, idx) => (
                            <tr key={item.participantId || idx}>
                              <td className="td-id">{item.participantId}</td>
                              <td className="td-name">{item.participantName || ""}</td>
                              <td>{item.dateOfSubmit || ""}</td>
                              <td>{item.mobileNumber || ""}</td>
                              <td>{item.emailId || ""}</td>
                              <td>{item.mrnOrHospitalId || ""}</td>
                              <td>{item.personTakingConsent || ""}</td>
                              <td>{item.nameOfTheHospital || ""}</td>
                              <td>
                                {item.isActive ? (
                                  <span className="badge badge-success">Active</span>
                                ) : (
                                  <span className="badge badge-secondary">Inactive</span>
                                )}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div className="table-pagination">
                    <span>Showing {paged.length} of {sorted.length} entries</span>
                    <div className="pagination-controls">
                      <button disabled={page === 0} onClick={() => setPage(0)}>First</button>
                      <button disabled={page === 0} onClick={() => setPage(page - 1)}>Prev</button>
                      <span className="page-indicator">Page {page + 1} / {totalPages || 1}</span>
                      <button disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>Next</button>
                      <button disabled={page >= totalPages - 1} onClick={() => setPage(totalPages - 1)}>Last</button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ===== ANALYTICS TAB ===== */}
            {tab === "analytics" && (
              <div className="dash-content fade-in">
                <div className="stats-row">
                  <StatCard icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>} label="Total" value={stats.total} color="#0066ff" />
                  <StatCard icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>} label="Active" value={stats.active} color="#00c853" />
                  <StatCard icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>} label="This Month" value={stats.thisMonth} color="#ff9800" />
                  <StatCard icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/></svg>} label="This Week" value={stats.thisWeek} color="#e53935" />
                </div>
                <div className="charts-row">
                  <BarChart data={stats.monthlyData} title="Submissions Trend" />
                  <DonutChart segments={stats.hospitalSegments} title="Hospital Distribution" total={stats.total} />
                </div>
                <div className="charts-row">
                  <BarChart data={stats.personData} title="Consent Takers Performance" />
                  <div className="chart-card">
                    <h4 className="chart-title">Quick Insights</h4>
                    <div className="insights-list">
                      <div className="insight-row">
                        <span className="insight-label">Avg. per month</span>
                        <span className="insight-value">{stats.monthlyData.length > 0 ? Math.round(stats.total / Math.max(stats.monthlyData.filter(d => d.value > 0).length, 1)) : 0}</span>
                      </div>
                      <div className="insight-row">
                        <span className="insight-label">Active rate</span>
                        <span className="insight-value">{stats.total > 0 ? `${Math.round((stats.active / stats.total) * 100)}%` : "N/A"}</span>
                      </div>
                      <div className="insight-row">
                        <span className="insight-label">Top hospital</span>
                        <span className="insight-value">{stats.hospitalSegments[0]?.label || "N/A"}</span>
                      </div>
                      <div className="insight-row">
                        <span className="insight-label">Top consent taker</span>
                        <span className="insight-value">{stats.personData[0]?.label || "N/A"}</span>
                      </div>
                      <div className="insight-row">
                        <span className="insight-label">Unique hospitals</span>
                        <span className="insight-value">{stats.uniqueHospitals}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
